'use client';

// Partie « À toi » d'une scène interactive : la question (QCM) et l'outil de
// la boîte à outils. Ils s'affichent DANS la scène (repère du canevas, mêmes
// couleurs que la vidéo quel que soit le thème de l'app) ; la scène attend
// l'élève jusqu'à « Continuer ».
//
// Le panneau tient dans la moitié basse de la scène (`PANEL_GEOMETRY` : la
// validation vérifie que le contexte de la question est au-dessus) et se
// replie (« Voir la scène ») pour tout revoir.

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Lightbulb, Wrench, X } from 'lucide-react';
import { JRAF_PALETTE, type JrafStep } from '@jeraisonne/animation';
import { cn } from '../../lib/utils';
import { AnimationMathText } from './animation-math';
import { SCENE_TARGET, SCENE_TEXT, SCENE_TEXT_LARGE, SCENE_TEXT_SMALL } from './scene-sizes';

type QuestionStep = Extract<JrafStep, { action: 'question.ask' }>;
type ToolStep = Extract<JrafStep, { action: 'tool.open' }>;

const C = JRAF_PALETTE;

function Panel({
  children,
  label,
  reopenLabel,
}: {
  children: React.ReactNode;
  label: string;
  /** bouton du panneau replié (« Revenir à la question ») */
  reopenLabel: string;
}) {
  // le panneau prend le focus à son ouverture : clavier et lecteurs d'écran y arrivent
  const ref = useRef<HTMLElement>(null);
  const reopenRef = useRef<HTMLButtonElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    // replié : le focus va au bouton « Revenir… », pas au conteneur (Espace y
    // mettrait la lecture en pause)
    if (collapsed) reopenRef.current?.focus({ preventScroll: true });
    else ref.current?.focus({ preventScroll: true });
  }, [collapsed]);
  if (collapsed)
    return (
      <section
        ref={ref}
        role="dialog"
        aria-label={label}
        tabIndex={-1}
        className="absolute inset-x-3 bottom-3 flex justify-center outline-none"
      >
        <button
          ref={reopenRef}
          type="button"
          onClick={() => setCollapsed(false)}
          className={cn(
            'flex items-center gap-2 rounded-full px-5 font-semibold shadow-2xl focus-visible:outline-none focus-visible:ring-4',
            SCENE_TARGET,
            SCENE_TEXT,
          )}
          style={{ backgroundColor: C.primary, color: C.paper }}
        >
          <ChevronUp size={20} aria-hidden="true" /> {reopenLabel}
        </button>
      </section>
    );
  return (
    <section
      ref={ref}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className="absolute inset-x-3 bottom-3 flex max-h-[50%] flex-col gap-3 overflow-y-auto rounded-2xl p-4 shadow-2xl outline-none"
      style={{ backgroundColor: C.paper, color: C.ink }}
    >
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        aria-label="Voir la scène"
        title="Voir la scène"
        className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-4"
        style={{ color: C.muted }}
      >
        <ChevronDown size={22} aria-hidden="true" />
      </button>
      {children}
    </section>
  );
}

function PrimaryButton({
  children,
  onClick,
  autoFocus,
}: {
  children: React.ReactNode;
  onClick: () => void;
  autoFocus?: boolean;
}) {
  return (
    <button
      type="button"
      autoFocus={autoFocus}
      onClick={onClick}
      className={cn(
        'rounded-xl px-4 font-semibold focus-visible:outline-none focus-visible:ring-4',
        SCENE_TARGET,
        SCENE_TEXT,
      )}
      style={{ backgroundColor: C.primary, color: C.paper }}
    >
      {children}
    </button>
  );
}

export function QuestionPanel({ step, onDone }: { step: QuestionStep; onDone: () => void }) {
  const [picked, setPicked] = useState<number | null>(null);
  const [wrong, setWrong] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const showHint = !revealed && wrong > 0 && step.hint !== undefined;

  const choose = (i: number) => {
    if (revealed) return;
    setPicked(i);
    if (i === step.correct) {
      setRevealed(true);
      return;
    }
    // une seconde chance avec l'indice, s'il y en a un
    if (wrong === 0 && step.hint) setWrong(1);
    else {
      setWrong((w) => w + 1);
      setRevealed(true);
    }
  };

  return (
    <Panel label="Question" reopenLabel="Revenir à la question">
      <p className={cn('pr-10 font-semibold leading-snug', SCENE_TEXT_LARGE)}>
        <AnimationMathText>{step.prompt}</AnimationMathText>
      </p>
      <ul className="flex flex-col gap-2">
        {step.choices.map((choice, i) => {
          const isCorrect = i === step.correct;
          const state =
            revealed && isCorrect ? 'correct' : picked === i && !isCorrect ? 'wrong' : 'idle';
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() => choose(i)}
                disabled={revealed}
                aria-pressed={picked === i}
                className={cn(
                  'flex w-full items-center gap-2 rounded-xl border-2 px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-4',
                  SCENE_TARGET,
                  SCENE_TEXT,
                )}
                style={{
                  borderColor:
                    state === 'correct' ? C.green : state === 'wrong' ? C.red : '#d9dde5',
                  backgroundColor:
                    state === 'correct' ? '#e8f8ef' : state === 'wrong' ? '#fdeceb' : C.paper,
                }}
              >
                <span className="flex-1">
                  <AnimationMathText>{choice}</AnimationMathText>
                </span>
                {state === 'correct' && (
                  <Check aria-label="bonne réponse" size={20} color={C.green} />
                )}
                {state === 'wrong' && <X aria-label="réponse fausse" size={20} color={C.red} />}
              </button>
            </li>
          );
        })}
      </ul>
      {showHint && (
        <p
          className={cn('flex items-start gap-2 rounded-xl p-3', SCENE_TEXT_SMALL)}
          style={{ backgroundColor: '#fff7df' }}
          role="status"
        >
          <Lightbulb size={18} className="mt-0.5 shrink-0" color={C.gold} aria-hidden="true" />
          <span>
            <AnimationMathText>{step.hint ?? ''}</AnimationMathText>
          </span>
        </p>
      )}
      {revealed && (
        <>
          <p className={cn('leading-snug', SCENE_TEXT_SMALL)} role="status">
            <strong style={{ color: picked === step.correct ? C.green : C.red }}>
              {picked === step.correct ? 'Bravo ! ' : 'Pas tout à fait. '}
            </strong>
            <AnimationMathText>{step.explanation}</AnimationMathText>
          </p>
          <PrimaryButton onClick={onDone} autoFocus>
            Continuer
          </PrimaryButton>
        </>
      )}
    </Panel>
  );
}

export function ToolPanel({
  step,
  onOpenTool,
  onDone,
}: {
  step: ToolStep;
  onOpenTool?: (toolId: string) => void;
  onDone: () => void;
}) {
  const [opened, setOpened] = useState(false);
  return (
    <Panel label="Outil" reopenLabel="Revenir à l’outil">
      <p
        className={cn('flex items-start gap-2 pr-10 font-semibold leading-snug', SCENE_TEXT_LARGE)}
      >
        <Wrench size={20} className="mt-1 shrink-0" color={C.primary} aria-hidden="true" />
        <span>
          <AnimationMathText>{step.prompt}</AnimationMathText>
        </span>
      </p>
      {onOpenTool ? (
        <PrimaryButton
          onClick={() => {
            setOpened(true);
            onOpenTool(step.toolId);
          }}
          autoFocus
        >
          Ouvrir l’outil
        </PrimaryButton>
      ) : (
        <p className={SCENE_TEXT_SMALL} style={{ color: C.muted }}>
          Cet outil s’ouvre dans l’application JeRaisonne.
        </p>
      )}
      <button
        type="button"
        onClick={onDone}
        className={cn(
          'rounded-xl border-2 px-4 font-semibold focus-visible:outline-none focus-visible:ring-4',
          SCENE_TARGET,
          SCENE_TEXT,
        )}
        style={{ borderColor: C.primary, color: C.primary }}
      >
        {opened ? 'Continuer' : 'Plus tard, continuer'}
      </button>
    </Panel>
  );
}
